import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, SALE_STATUS_LABEL, type DashboardData } from "@/lib/admin";
import { formatPln } from "@/lib/utils";

export function AdminDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    adminApi
      .dashboard()
      .then((d) => {
        if (active) setData(d);
      })
      .catch((e) => {
        if (active) setErr(e.message);
      });
    return () => {
      active = false;
    };
  }, []);

  if (err) return <ErrorBox message={err} />;
  if (!data) return <p className="font-mono text-sm">Ładuję dashboard…</p>;

  const totalRevenue = data.last30Days.reduce(
    (a, b) => a + (b.status === 3 || b.status === 4 ? b.sum : 0),
    0,
  );
  const totalAnswered = data.last30Days.find((s) => s.status === 3)?.count ?? 0;
  const totalPending = data.last30Days.find((s) => s.status === 1)?.count ?? 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display font-bold text-3xl">Dashboard</h1>
        <p className="text-sm text-carp-ink/70 font-mono">ostatnie 30 dni</p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Przychód (Answered)" value={formatPln(totalRevenue)} tone="lime" />
        <Card label="Odpowiedziane" value={String(totalAnswered)} tone="cyan" />
        <Card label="Oczekujące" value={String(totalPending)} tone="yellow" />
        <Card
          label="Użytkowników (adminów)"
          value={`${data.users.total} (${data.users.admins})`}
          tone="rose"
        />
      </div>

      <section className="brut bg-carp-paper p-5">
        <h2 className="font-display font-bold text-xl mb-3">Sprzedaże wg statusu</h2>
        <table className="w-full text-sm">
          <thead className="text-left font-mono text-xs uppercase text-carp-ink/60">
            <tr>
              <th className="pb-2">Status</th>
              <th className="pb-2 text-right">Liczba</th>
              <th className="pb-2 text-right">Suma</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-carp-ink/10">
            {data.last30Days.map((s) => (
              <tr key={s.status}>
                <td className="py-2 font-display font-semibold">
                  {SALE_STATUS_LABEL[s.status] ?? s.status}
                </td>
                <td className="py-2 text-right font-mono">{s.count}</td>
                <td className="py-2 text-right font-mono">{formatPln(s.sum)}</td>
              </tr>
            ))}
            {data.last30Days.length === 0 && (
              <tr>
                <td colSpan={3} className="py-4 text-center text-carp-ink/50">
                  Brak sprzedaży w ostatnich 30 dniach
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {data.lastSale && (
        <section className="brut bg-carp-paper p-5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display font-bold text-xl">Ostatnia sprzedaż</h2>
            <Link to={`/admin/sales/${data.lastSale.id}`} className="link-brut text-sm">
              Otwórz →
            </Link>
          </div>
          <p className="text-sm">
            <b>{formatPln(data.lastSale.amount)}</b> · {data.lastSale.email} ·{" "}
            <span className="font-mono text-xs text-carp-ink/60">
              {new Date(data.lastSale.createdAt).toLocaleString("pl-PL")}
            </span>
          </p>
        </section>
      )}

      <p className="text-xs font-mono text-carp-ink/50">
        Kick events ostatnie 24h: {data.kickEventsLast24h}
      </p>
    </div>
  );
}

function Card({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "lime" | "cyan" | "yellow" | "rose";
}) {
  const bg =
    tone === "lime"
      ? "bg-carp-lime"
      : tone === "cyan"
        ? "bg-carp-cyan"
        : tone === "yellow"
          ? "bg-carp-yellow"
          : "bg-carp-secondary";
  return (
    <div className={`brut ${bg} p-4`}>
      <p className="text-[10px] font-display font-bold uppercase tracking-wide opacity-80">
        {label}
      </p>
      <p className="font-display font-bold text-2xl mt-1">{value}</p>
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="brut bg-carp-destructive text-white p-4">
      <p className="font-display font-bold uppercase">Błąd</p>
      <p className="text-sm">{message}</p>
    </div>
  );
}
